import styled from "styled-components";

import { homeDark } from "@layout/theme/homeTokens";
import { GRAIN_URL, home } from "./tokens";

const darkVars = Object.entries(homeDark)
  .map(([key, value]) => `--hm-${key}: ${value};`)
  .join("\n");

export const Page = styled.div`
  position: relative;
  min-height: 100vh;
  font-family: ${home.sans};
  color: ${home.ink};
  -webkit-font-smoothing: antialiased;
  overflow: hidden;

  /* The mesh is painted once, viewport-fixed, by the global ground style –
     painting it again here (element-sized) creates a visible seam where
     the page ends and the body shows through. */
  background: transparent;
`;

/* Film grain over the whole ground, under all content. */
export const Grain = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  opacity: ${home.grainOpacity};
  background-image: ${GRAIN_URL};
  mix-blend-mode: overlay;
`;

export const Content = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1440px;
  margin: 0 auto;
`;

/**
 * A grain-textured painted panel that stays dark in both themes, like the
 * art cards on the marketing site. It re-declares the design-system
 * variables to the dark set, so anything rendered inside resolves its
 * colors correctly with no per-element overrides.
 */
export const ArtPanel = styled.div`
  ${darkVars}
  position: relative;
  color: var(--hm-ink);
  border-radius: ${home.radius}px;
  border: 1px solid rgba(242, 239, 233, 0.12);
  overflow: hidden;
  transition: border-color 160ms ease;

  &:hover {
    border-color: rgba(242, 239, 233, 0.18);
  }

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    background-image: ${GRAIN_URL};
    opacity: 0.19;
    mix-blend-mode: overlay;
  }
`;

export const Card = styled.div`
  border-radius: ${home.radius}px;
  border: 1px solid ${home.line10};
  background: ${home.cardBg};
  transition: border-color 160ms ease;

  &:hover {
    border-color: ${home.line18};
  }
`;

export const Eyebrow = styled.div`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: ${home.copper};
`;

export const SerifTitle = styled.div<{ $size?: number }>`
  font-family: ${home.serif};
  font-size: ${({ $size }) => $size ?? 33}px;
  line-height: 1.1;
  letter-spacing: -0.4px;
  color: ${home.ink};
  text-wrap: pretty;

  em {
    font-style: italic;
    color: ${home.copper};
  }
`;

/** The one filled button per surface. */
export const FilledButton = styled.button`
  height: 44px;
  display: flex;
  align-items: center;
  padding: 0 22px;
  border: none;
  border-radius: 999px;
  background: ${home.ink};
  color: ${home.ground};
  font-family: ${home.sans};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 160ms ease;

  &:hover {
    filter: brightness(1.08);
  }
`;

export const GhostButton = styled.button`
  height: 44px;
  display: flex;
  align-items: center;
  padding: 0 20px;
  border-radius: 999px;
  border: 1px solid ${home.line14};
  background: transparent;
  color: ${home.ink80};
  font-family: ${home.sans};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 160ms ease;

  &:hover {
    background: ${home.fill09};
  }
`;

export const Section = styled.div`
  padding: 44px ${home.pagePad} 8px;
  display: flex;
  flex-direction: column;
  gap: 26px;
`;

export const ThreeUp = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 20px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 720px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const AvatarCircle = styled.div<{ $tone: "copper" | "sage"; $size?: number }>`
  width: ${({ $size }) => $size ?? 36}px;
  height: ${({ $size }) => $size ?? 36}px;
  border-radius: 999px;
  background: ${({ $tone }) => ($tone === "copper" ? home.avatarCopperBg : home.avatarSageBg)};
  border: 1px solid
    ${({ $tone }) => ($tone === "copper" ? home.avatarCopperBorder : home.avatarSageBorder)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 600;
  color: ${({ $tone }) => ($tone === "copper" ? home.copperText : home.sage)};
  flex-shrink: 0;
`;
